import React, { useMemo } from 'react';
import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import { SliceZone } from '@prismicio/react';

import Layout from '@/components/Layout';
import { Posts } from '@/components/Posts/Posts';
import useFilteredAndPaginatedPosts from '@/hooks/useFilteredAndPaginatedPosts';
import components from '../slices';

const PostsTemplate = ({ data, pageContext }) => {
  const {
    body: slices,
    body1: socialBody,
    splash_link_text: splashLinkText,
  } = data.prismicBlogHomePage.data;

  // html head data
  const headData = useMemo(
    () =>
      socialBody.map(({ primary: { description, image, title } }) => ({
        title: title.text,
        description: description.text,
        ogTitle: title.text,
        ogDescription: description.text,
        ogImageUrl: image.url,
      }))[0],
    [socialBody],
  );

  // all posts
  const posts = useMemo(() =>
    data.allPrismicBlogPost.nodes.map((post) => ({
      url: post.url,
      image: post.data.blog_post_image,
      title: post.data.blog_post_title,
      description: post.data.blog_post_description,
      tags: post.data.blog_post_tag.text
        ? post.data.blog_post_tag.text.split(',').map((tag) => tag.trim())
        : [],
    })),
  );

  const {
    list,
    loadRef,
    tags,
    selectedTag,
    setSelectedTag,
    filteredPosts,
    hasMore,
  } = useFilteredAndPaginatedPosts(posts);

  return (
    <Layout
      {...headData}
      lang={pageContext.lang}
      altLangs={pageContext.alternate_languages}
    >
      <SliceZone
        slices={slices}
        components={components}
        context={{
          scrollLink: { to: 'posts', text: splashLinkText || 'See all posts' },
          tags,
          selectedTag,
          setSelectedTag,
        }}
      />
      <Posts
        lang={data.prismicBlogHomePage.lang}
        filteredPosts={filteredPosts}
        list={list}
        hasMore={hasMore}
        ref={loadRef}
      />
    </Layout>
  );
};

export const query = graphql`
  query PostsQuery($lang: String, $uid: String) {
    prismicBlogHomePage(lang: { eq: $lang }, uid: { eq: $uid }) {
      _previewable
      lang
      alternate_languages {
        uid
        type
        lang
      }
      data {
        splash_link_text
        body1 {
          ... on PrismicBlogHomePageDataBody1GeneralCard {
            primary {
              description {
                text
              }
              title {
                text
              }
              image {
                url
              }
            }
          }
          ... on PrismicBlogHomePageDataBody1TwitterCard {
            primary {
              description {
                text
              }
              title {
                text
              }
              image {
                url
              }
            }
          }
        }
        body {
          ... on PrismicSliceType {
            id
            slice_type
            slice_label
          }
          ...BlogHomePageSplashSection
          ...BlogHomePageFeaturedPosts
        }
      }
    }

    allPrismicBlogPost(
      filter: { lang: { eq: $lang } }
      sort: { order: DESC, fields: first_publication_date }
    ) {
      nodes {
        url
        id
        data {
          blog_post_title {
            richText
          }
          blog_post_description {
            richText
          }
          blog_post_image {
            alt
            gatsbyImageData(placeholder: BLURRED)
          }
          blog_post_tag {
            text
          }
        }
      }
    }
  }
`;

export default withPrismicPreview(PostsTemplate);
